import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../../components/Icon';
import { playSound } from '../../audio/sound';
import type { QuizItem } from './types';
import { splitMatchChoices, type Draft } from './draft';
import { SafeSvg, looksLikeSvg } from '../../components/SafeSvg';

export interface AnswerInputProps {
  item: QuizItem;
  draft: Draft;
  onChange: (draft: Draft) => void;
  disabled?: boolean;
  /** Після відповіді підсвічуємо, що саме обрала дитина. */
  revealed?: { correctAnswer: string; isCorrect: boolean } | null;
}

/** Один вхід для всіх семи форматів із ITEM_FORMATS. */
export function AnswerInput(props: AnswerInputProps): React.JSX.Element {
  const { item } = props;
  switch (item.format) {
    case 'multi':
      return <MultiChoice {...props} />;
    case 'numeric':
      return <NumericPad {...props} />;
    case 'text':
      return <TextAnswer {...props} />;
    case 'order':
      return <OrderAnswer {...props} />;
    case 'match':
      return <MatchAnswer {...props} />;
    case 'cloze':
      return item.choices && item.choices.length > 0 ? (
        <SingleChoice {...props} />
      ) : (
        <TextAnswer {...props} />
      );
    case 'mcq':
    default:
      return <SingleChoice {...props} />;
  }
}

// ───────────────────────────── mcq / cloze ─────────────────────────────

function SingleChoice({
  item,
  draft,
  onChange,
  disabled,
  revealed,
}: AnswerInputProps): React.JSX.Element {
  const selected = draft.kind === 'choice' ? draft.choiceId : null;
  return (
    <div className="flex flex-col gap-2.5" role="radiogroup" aria-label="Варіанти відповіді">
      {(item.choices ?? []).map((choice, index) => {
        const isSelected = selected === choice.id;
        const state = revealed
          ? isSelected
            ? revealed.isCorrect
              ? 'correct'
              : 'wrong'
            : revealed.correctAnswer === choice.text || revealed.correctAnswer === choice.id
              ? 'correct'
              : 'idle'
          : isSelected
            ? 'selected'
            : 'idle';
        return (
          <button
            key={choice.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            data-state={state}
            data-testid={`choice-${choice.id}`}
            className="lk-option"
            onClick={() => {
              playSound('tap');
              onChange({ kind: 'choice', choiceId: choice.id });
            }}
          >
            <span className="lk-chip h-8 w-8 shrink-0 justify-center p-0 text-sm">
              {String.fromCharCode(65 + index)}
            </span>
            <ChoiceBody choice={choice} itemId={item.id} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Вміст варіанта: текст або малюнок.
 *
 * Частина логічних генераторів (обертання фігур, розгортки, матриці) віддає
 * варіанти саме картинкою — без неї питання «яка фігура та сама після
 * повороту» перетворюється на вибір між літерами А, Б, В, Г наосліп.
 */
function ChoiceBody({
  choice,
  itemId,
}: {
  choice: { id: string; text: string; image?: string };
  /** Потрібен, щоб повідомити про малюнок, який не відмалювався. */
  itemId?: string;
}): React.JSX.Element {
  if (looksLikeSvg(choice.image)) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <SafeSvg
          markup={choice.image!}
          label={choice.text}
          className="h-16 w-16 shrink-0"
          itemId={itemId}
          field="choice"
        />
        <span className="min-w-0 flex-1 text-left">{choice.text}</span>
      </span>
    );
  }
  return <span className="min-w-0 flex-1">{choice.text}</span>;
}

// ──────────────────────────────── multi ────────────────────────────────

function MultiChoice({ item, draft, onChange, disabled }: AnswerInputProps): React.JSX.Element {
  const selected = draft.kind === 'choices' ? draft.choiceIds : [];
  return (
    <div className="flex flex-col gap-2.5">
      <p className="text-xs font-bold text-white/50">Можна обрати кілька відповідей.</p>
      {(item.choices ?? []).map((choice) => {
        const isSelected = selected.includes(choice.id);
        return (
          <button
            key={choice.id}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            disabled={disabled}
            data-state={isSelected ? 'selected' : 'idle'}
            data-testid={`choice-${choice.id}`}
            className="lk-option"
            onClick={() => {
              playSound('tap');
              onChange({
                kind: 'choices',
                choiceIds: isSelected
                  ? selected.filter((id) => id !== choice.id)
                  : [...selected, choice.id],
              });
            }}
          >
            <span
              className={cn(
                'grid h-7 w-7 shrink-0 place-items-center rounded-lg border-2',
                isSelected
                  ? 'border-[var(--lk-gold)] bg-[var(--lk-gold)]'
                  : 'border-white/30',
              )}
              aria-hidden
            >
              {isSelected ? <Icon name="correct" size={18} /> : null}
            </span>
            <ChoiceBody choice={choice} itemId={item.id} />
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────── numeric ───────────────────────────────

const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '0', '⌫'] as const;

function NumericPad({ item, draft, onChange, disabled }: AnswerInputProps): React.JSX.Element {
  const raw = draft.kind === 'numeric' ? draft.raw : '';

  const push = (key: string): void => {
    if (disabled) return;
    playSound('tap');
    if (key === '⌫') {
      onChange({ kind: 'numeric', raw: raw.slice(0, -1) });
      return;
    }
    if (key === ',' && raw.includes(',')) return;
    onChange({ kind: 'numeric', raw: raw + key });
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="lk-panel-tight flex items-center gap-2 px-4 py-3">
        <input
          inputMode="decimal"
          autoComplete="off"
          value={raw}
          disabled={disabled}
          onChange={(event) =>
            onChange({ kind: 'numeric', raw: event.target.value.replace(/[^\d,.-]/g, '') })
          }
          placeholder="Введи число"
          aria-label="Числова відповідь"
          data-testid="numeric-input"
          className="tabular min-w-0 flex-1 bg-transparent text-3xl font-black outline-none"
        />
        {item.unit ? <span className="text-lg font-black text-white/50">{item.unit}</span> : null}
      </label>

      <div className="grid grid-cols-3 gap-2">
        {PAD_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            disabled={disabled}
            className="lk-btn h-14 text-2xl"
            onClick={() => push(key)}
            aria-label={key === '⌫' ? 'Стерти' : key === ',' ? 'Кома' : `Цифра ${key}`}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          className="lk-btn lk-btn--ghost col-span-3 h-12 text-lg"
          onClick={() => {
            playSound('tap');
            onChange({ kind: 'numeric', raw: raw.startsWith('-') ? raw.slice(1) : `-${raw}` });
          }}
          aria-label="Змінити знак"
        >
          ± мінус
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────── text ────────────────────────────────

function TextAnswer({ item, draft, onChange, disabled }: AnswerInputProps): React.JSX.Element {
  const value = draft.kind === 'text' ? draft.value : '';
  return (
    <label className="lk-panel-tight flex flex-col gap-1 px-4 py-3">
      <span className="text-[11px] font-black tracking-widest text-white/45 uppercase">
        Твоя відповідь
      </span>
      <input
        type="text"
        value={value}
        disabled={disabled}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        maxLength={300}
        placeholder={item.format === 'cloze' ? 'Встав пропущене слово' : 'Напиши відповідь'}
        onChange={(event) => onChange({ kind: 'text', value: event.target.value })}
        data-testid="text-input"
        className="w-full bg-transparent text-2xl font-black outline-none"
      />
    </label>
  );
}

// ──────────────────────────────── order ────────────────────────────────

function OrderAnswer({ item, draft, onChange, disabled }: AnswerInputProps): React.JSX.Element {
  const sequence = draft.kind === 'order' ? draft.sequence : [];
  const choices = item.choices ?? [];
  // Залежність — саме `item.choices`, а не похідний `choices`: коли варіантів
  // немає, `?? []` створює новий масив на кожен рендер, і мемо перераховується
  // без потреби.
  const byId = useMemo(
    () => new Map((item.choices ?? []).map((c) => [c.id, c.text])),
    [item.choices],
  );
  const remaining = choices.filter((choice) => !sequence.includes(choice.id));
  const [dragging, setDragging] = useState<string | null>(null);

  const move = (index: number, delta: number): void => {
    const target = index + delta;
    if (target < 0 || target >= sequence.length) return;
    const next = [...sequence];
    const [moved] = next.splice(index, 1);
    if (moved === undefined) return;
    next.splice(target, 0, moved);
    playSound('tap');
    onChange({ kind: 'order', sequence: next });
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-bold text-white/50">
        Натискай по черзі — або перетягуй, щоб змінити порядок.
      </p>

      <ol className="flex flex-col gap-2" aria-label="Твій порядок">
        {sequence.map((id, index) => (
          <li
            key={id}
            draggable={!disabled}
            onDragStart={() => setDragging(id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (!dragging || dragging === id) return;
              const next = sequence.filter((x) => x !== dragging);
              next.splice(index, 0, dragging);
              setDragging(null);
              onChange({ kind: 'order', sequence: next });
            }}
            className="lk-option cursor-grab"
            data-state="selected"
            data-testid={`ordered-${id}`}
          >
            <span className="lk-chip h-8 w-8 shrink-0 justify-center p-0">{index + 1}</span>
            <span className="min-w-0 flex-1">{byId.get(id) ?? id}</span>
            <span className="flex shrink-0 gap-1">
              <button
                type="button"
                className="lk-chip h-9 w-9 justify-center p-0"
                onClick={() => move(index, -1)}
                disabled={disabled || index === 0}
                aria-label="Вище"
              >
                ↑
              </button>
              <button
                type="button"
                className="lk-chip h-9 w-9 justify-center p-0"
                onClick={() => move(index, 1)}
                disabled={disabled || index === sequence.length - 1}
                aria-label="Нижче"
              >
                ↓
              </button>
              <button
                type="button"
                className="lk-chip h-9 w-9 justify-center p-0"
                onClick={() => onChange({ kind: 'order', sequence: sequence.filter((x) => x !== id) })}
                disabled={disabled}
                aria-label="Прибрати"
              >
                <Icon name="close" size={14} />
              </button>
            </span>
          </li>
        ))}
      </ol>

      {remaining.length > 0 ? (
        <div className="flex flex-col gap-2">
          {remaining.map((choice) => (
            <button
              key={choice.id}
              type="button"
              disabled={disabled}
              className="lk-option"
              data-testid={`choice-${choice.id}`}
              onClick={() => {
                playSound('tap');
                onChange({ kind: 'order', sequence: [...sequence, choice.id] });
              }}
            >
              <span className="lk-chip h-8 w-8 shrink-0 justify-center p-0">＋</span>
              <ChoiceBody choice={choice} itemId={item.id} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────── match ────────────────────────────────

function MatchAnswer({ item, draft, onChange, disabled }: AnswerInputProps): React.JSX.Element {
  const pairs = draft.kind === 'match' ? draft.pairs : [];
  const { left, right } = useMemo(() => splitMatchChoices(item.choices), [item.choices]);
  const [activeLeft, setActiveLeft] = useState<string | null>(null);

  const textOf = (id: string): string =>
    [...left, ...right].find((choice) => choice.id === id)?.text ?? id;

  const pairedLeft = new Set(pairs.map(([l]) => l));
  const pairedRight = new Set(pairs.map(([, r]) => r));

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-bold text-white/50">
        Натисни зліва, потім справа — і пара готова.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-2">
          {left.map((choice) => (
            <button
              key={choice.id}
              type="button"
              disabled={disabled || pairedLeft.has(choice.id)}
              data-state={activeLeft === choice.id ? 'selected' : 'idle'}
              data-testid={`match-left-${choice.id}`}
              className={cn('lk-option text-sm', pairedLeft.has(choice.id) && 'opacity-40')}
              onClick={() => {
                playSound('tap');
                setActiveLeft(choice.id);
              }}
            >
              {choice.text}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          {right.map((choice) => (
            <button
              key={choice.id}
              type="button"
              disabled={disabled || pairedRight.has(choice.id) || activeLeft === null}
              data-testid={`match-right-${choice.id}`}
              className={cn('lk-option text-sm', pairedRight.has(choice.id) && 'opacity-40')}
              onClick={() => {
                if (!activeLeft) return;
                playSound('tap');
                onChange({ kind: 'match', pairs: [...pairs, [activeLeft, choice.id]] });
                setActiveLeft(null);
              }}
            >
              {choice.text}
            </button>
          ))}
        </div>
      </div>

      {pairs.length > 0 ? (
        <ul className="flex flex-col gap-1.5" aria-label="Твої пари">
          {pairs.map(([l, r]) => (
            <li
              key={`${l}-${r}`}
              className="lk-panel-tight flex items-center gap-2 px-3 py-2 text-sm font-bold"
            >
              <span className="min-w-0 flex-1 truncate">
                {textOf(l)} <span className="text-white/40">→</span> {textOf(r)}
              </span>
              <button
                type="button"
                className="lk-chip shrink-0"
                disabled={disabled}
                onClick={() =>
                  onChange({
                    kind: 'match',
                    pairs: pairs.filter(([pl, pr]) => !(pl === l && pr === r)),
                  })
                }
                aria-label="Прибрати пару"
              >
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
